import { parseEgressConfig } from '@nadoutong/contracts';
import type {
  EgressAdmissionBlacklist,
  EgressConfig,
  EgressFirewallRef,
} from '@nadoutong/contracts';
import {
  armedBlacklistRules,
  composeBindingBlacklistOverride,
  type StoredBindingOverride,
} from './egressAdmissionBlacklist.ts';

/**
 * The binding-level ① firewall reference, and the one composer both editable
 * axes of a binding override now go through (#1760).
 *
 * A binding override is a whole-document snapshot — `PUT
 * .../key-buckets/:id/channels` runs
 * `serializeEgressConfig(parseEgressConfig(...))` and takes nothing less — so a
 * second editable axis is not a second field. Every edit on either axis rewrites
 * the same document, and two independent composers would each drop what the
 * other wrote: unticking the last blacklist rule would delete a firewall
 * reference, and arming a firewall would make the blacklist control read-only
 * because the stored document no longer reproduces from the profile alone.
 * Hence one composer taking both axes, and a state function that reproduces the
 * stored override the same way.
 *
 * Why a binding-level reference is needed at all, and not just the profile-level
 * one: one profile backs many bindings, and a ruleset that is right for one SKU
 * namespace can refuse another namespace's correct traffic. Without this, arming
 * a firewall is all-or-nothing across every binding under the profile.
 */

/**
 * Reference equality by value.
 *
 * `version: undefined` and an absent `version` are the same reference (follow
 * `active_version`), which is also how `parseEgressConfig` normalises it.
 */
export function sameFirewallRef(
  a: EgressFirewallRef | null,
  b: EgressFirewallRef | null,
): boolean {
  if (a === null || b === null) return a === b;
  return a.rulesetId === b.rulesetId && (a.version ?? null) === (b.version ?? null);
}

/**
 * `ok: false` is a refusal, never a clear.
 *
 * The two outcomes a caller must not confuse are "the composition does not
 * parse" and "no override is needed", and both used to spell `null`. A failed
 * composition read as "clear the override" is the deletion #1536 exists to
 * stop, so the failure gets its own arm and `override: null` means only the
 * second thing.
 */
export type ComposeBindingOverrideResult =
  | { readonly ok: true; readonly override: EgressConfig | null }
  | { readonly ok: false };

/**
 * Compose the full override document from both editable axes.
 *
 * Layered on `composeBindingBlacklistOverride` rather than replacing it: the
 * admission-mode reasoning there (which profile modes may be composed over, and
 * why the others may not) is #1609's, it is load-bearing, and it is unchanged.
 * This adds the firewall reference on top of whatever that produced.
 *
 * `override: null` — no override needed — requires BOTH axes to be at the
 * profile's own value. That is why the blacklist's own `null` is not enough on
 * its own: a binding with no armed rule but a firewall reference of its own
 * still needs a document.
 */
export function composeBindingEgressOverride(
  profileConfig: EgressConfig,
  axes: {
    readonly blacklist: EgressAdmissionBlacklist | null;
    readonly firewall: EgressFirewallRef | null;
  },
): ComposeBindingOverrideResult {
  const blacklistDoc = composeBindingBlacklistOverride(profileConfig, axes.blacklist);
  if (blacklistDoc === null && armedBlacklistRules(axes.blacklist).length > 0) {
    // The blacklist composer returns `null` both for "nothing armed" and for
    // "did not parse"; its own doc comment requires callers to separate them by
    // asking whether anything was armed.
    return { ok: false };
  }
  if (blacklistDoc === null && sameFirewallRef(profileConfig.firewall ?? null, axes.firewall)) {
    return { ok: true, override: null };
  }
  try {
    // Round-tripped through the strict parser for the same reason the blacklist
    // composer does it: this document goes to a write face that parses it
    // again, and a composition that cannot parse has to surface here rather
    // than as a save-time error naming a path nobody typed.
    return {
      ok: true,
      override: parseEgressConfig({
        ...(blacklistDoc ?? profileConfig),
        firewall: axes.firewall ?? undefined,
      }),
    };
  } catch {
    return { ok: false };
  }
}

/**
 * The profile document as the blacklist control should see it for a binding
 * carrying `firewall`.
 *
 * `bindingBlacklistState` decides editable-vs-read-only by reproducing the
 * stored override from the profile and comparing whole documents. A binding
 * firewall reference the profile does not have is a difference it would read as
 * "someone edited fields beyond the blacklist" and go read-only on — which would
 * mean arming a firewall silently takes the blacklist checkboxes away. Folding
 * the reference into the base it compares against keeps the two axes
 * independent without teaching that function about this one.
 *
 * Nothing else about the profile is touched, and the gating it does on `lane`
 * and `admission.mode` is unaffected by the fold.
 */
export function profileWithBindingFirewall(
  profileConfig: EgressConfig | null | undefined,
  firewall: EgressFirewallRef | null,
): EgressConfig | null | undefined {
  if (profileConfig === null || profileConfig === undefined) return profileConfig;
  if (sameFirewallRef(profileConfig.firewall ?? null, firewall)) return profileConfig;
  try {
    return parseEgressConfig({ ...profileConfig, firewall: firewall ?? undefined });
  } catch {
    return profileConfig;
  }
}

export type BindingFirewallUnavailableReason = 'no-profile' | 'override-fragment';

export type BindingFirewallState =
  /** No control: this binding cannot carry a composed override at all. */
  | { readonly kind: 'unavailable'; readonly reason: BindingFirewallUnavailableReason }
  /** Editable: the stored override (if any) is one this editor reproduces. */
  | { readonly kind: 'editable'; readonly firewall: EgressFirewallRef | null }
  /** Read-only, for the same reason as the blacklist's `diverged`. */
  | { readonly kind: 'diverged'; readonly firewall: EgressFirewallRef | null };

/**
 * Which firewall control the selected binding gets, if any.
 *
 * Gated less than the blacklist is, and deliberately: the blacklist composes by
 * rewriting `admission.mode`, so it has to refuse the modes where that rewrite
 * would mean more than the checkbox says, and it is `claude-code`-only because
 * `require-cc-black` is. A firewall reference adds one key and is protocol- and
 * lane-agnostic — ① runs before ② for every lane — so the only things that can
 * stop it are the two that stop any override: no profile to compose over, and a
 * stored fragment that a whole-document rewrite would destroy.
 */
export function bindingFirewallState(
  profileConfig: EgressConfig | null | undefined,
  override: StoredBindingOverride | null,
): BindingFirewallState {
  if (profileConfig === null || profileConfig === undefined) {
    return { kind: 'unavailable', reason: 'no-profile' };
  }
  if (override === null) {
    // No override: the binding runs the profile's own reference, and that is
    // what the control shows — not "none", which would read as "this binding
    // skips ①" when it does not.
    return { kind: 'editable', firewall: profileConfig.firewall ?? null };
  }
  if (override.config === null) {
    return { kind: 'unavailable', reason: 'override-fragment' };
  }
  const firewall = override.config.firewall ?? null;
  const reproduced = composeBindingEgressOverride(profileConfig, {
    blacklist: override.config.admission?.blacklist ?? null,
    firewall,
  });
  const reproduces = reproduced.ok
    && JSON.stringify(reproduced.override) === JSON.stringify(override.config);
  return reproduces ? { kind: 'editable', firewall } : { kind: 'diverged', firewall };
}

/**
 * What one binding may reference: every shared ruleset, plus the
 * binding-scoped ones this exact (key bucket × channel) owns.
 *
 * A binding-scoped ruleset belonging to a SIBLING channel is deliberately not
 * offered. The runtime resolves a reference by id alone and would honour it,
 * which is precisely why the picker must not hand one over: `scope: 'binding'`
 * would stop meaning anything the moment a second binding could point at it.
 *
 * Both lists arrive `undefined` while loading. Treating that as "empty" is
 * right here and only here: the caller keeps rendering a stored reference that
 * is in neither list, so a slow load narrows the choices without ever making a
 * live reference look absent.
 */
export function selectableFirewallRulesets<
  T extends { readonly owner_channel_id: string | null },
>(
  shared: readonly T[] | undefined,
  ownedByBucket: readonly T[] | undefined,
  channelId: string | null,
): readonly T[] {
  const mine = channelId === null
    ? []
    : (ownedByBucket ?? []).filter((ruleset) => ruleset.owner_channel_id === channelId);
  return [...(shared ?? []), ...mine];
}
